import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MultiSelect } from "@/components/ui";
import type { BookingLifecycleStatus } from "@/types";
import { cn } from "@/lib/utils";
import { BOOKING_STATUS_FILTER_OPTIONS } from "./bookingStatusFilter";

function statusOptionLabel(
  status: BookingLifecycleStatus,
  t: (key: string) => string,
): string {
  if (status === "PENDING_HANDSHAKE") {
    return t("partnerStatus.pending");
  }
  if (status === "ACCEPTED") {
    return t("partnerStatus.confirmed");
  }
  return t(`status.${status}`);
}

interface BookingsStatusFilterProps {
  value: BookingLifecycleStatus[];
  onChange: (value: BookingLifecycleStatus[]) => void;
  statusCounts: Record<BookingLifecycleStatus, number>;
  className?: string;
}

export function BookingsStatusFilter({
  value,
  onChange,
  statusCounts,
  className,
}: BookingsStatusFilterProps) {
  const { t } = useTranslation("bookings");
  const { t: tComponents } = useTranslation("components");

  const options = useMemo(
    () =>
      BOOKING_STATUS_FILTER_OPTIONS.map((status) => ({
        value: status,
        label: t("statusFilter.optionWithCount", {
          name: statusOptionLabel(status, t),
          count: statusCounts[status],
        }),
      })),
    [statusCounts, t],
  );

  return (
    <MultiSelect
      label={t("statusFilter.label")}
      className={cn(className)}
      options={options}
      value={value}
      onChange={(next) => onChange(next as BookingLifecycleStatus[])}
      placeholderLabel={t("statusFilter.any")}
      removeChipLabel={(optionLabel) =>
        tComponents("multiSelectRemoveChip", { label: optionLabel })
      }
    />
  );
}
